// React imports
import React, { useState, type JSX } from "react";
// Import typed Redux hooks
import { useAppDispatch } from "./hooks/reduxHooks";

// Import child components
import MobileDealsView from "./components/deals/MobileDealsView";
import DesktopDealsView from "./components/deals/DesktopDealsView";
import WorkspaceTabContent from "./components/deals/WorkspaceTabContent";
import type { WorkspaceTabBarProps } from "./components/deals/WorkspaceTabBar";

// Import workspace hooks and Redux actions
import { useChatPanelResize } from "./hooks/useChatPanelResize";
import { useWorkspaceEntrance } from "./hooks/useWorkspaceEntrance";
import { useWorkspaceTabs, type WorkspaceTabs } from "./hooks/useWorkspaceTabs";
import { useWorkspaceClosers, type WorkspaceClosers } from "./hooks/useWorkspaceClosers";
import {
  useDealSelection,
  type DealSelection,
  type ExpertSelection,
  type WorkspaceResults,
  useExpertSelection,
  useWorkspaceResults,
} from "./hooks/useWorkspaceSelection";
import type { AppDispatch } from "./types/redux";
import { setSelectedDealTab } from "./DealsTableSlice_Store";

/** Count badge value for a base tab. */
function tabCount(tab: string, dealCount: number, documentCount: number): number {
  if (tab === "Deals") return dealCount;
  if (tab === "Docs") return documentCount;
  return 0;
}

interface TabBarContext {
  deal: DealSelection;
  expert: ExpertSelection;
  results: WorkspaceResults;
  tabState: WorkspaceTabs;
  closers: WorkspaceClosers;
  dispatch: AppDispatch;
}

/** Everything the tab strip needs, shared by the mobile and desktop views. */
function buildTabBarProps(ctx: TabBarContext): WorkspaceTabBarProps {
  const { deal, expert, results, tabState, closers, dispatch } = ctx;
  return {
    hasDealSelected: deal.hasDealSelected,
    hasExpertSelected: expert.hasExpertSelected,
    expertName: expert.expertName,
    dealName: deal.dealName,
    dealDocsCount: deal.dealDocsCount,
    activeTab: tabState.activeTab,
    baseTabs: tabState.baseTabs,
    documentTabs: tabState.documentTabs,
    getTabCount: (tab) => tabCount(tab, results.dealCount, results.documentCount),
    onSelectTab: tabState.setActiveTab,
    onSelectDealTab: (tab) => {
      dispatch(setSelectedDealTab(tab));
      tabState.setActiveTab(tab === "docs" ? "DealDocs" : "DealSummary");
    },
    onCloseDeal: closers.closeDeal,
    onCloseExpert: closers.closeExpert,
    onCloseDocument: closers.closeDocument,
  };
}

// Default export of DealsLayout_Component
export default function DealsLayout_Component(): JSX.Element {
  const dispatch = useAppDispatch();
  // Mobile view state - true for chat, false for results
  const [showChat, setShowChat] = useState<boolean>(true);

  const resize = useChatPanelResize();
  const deal = useDealSelection();
  const expert = useExpertSelection(deal.inlineDealsFlag);
  const results = useWorkspaceResults(
    deal.inlineDealsFlag,
    deal.hasDealSelected,
    expert.hasExpertSelected
  );
  const entrance = useWorkspaceEntrance(
    results.hasResults,
    results.layoutMode,
    resize.splitLayoutRef,
    // On mobile, flip back to the Chat view for a new conversation
    () => setShowChat(true)
  );

  // On mobile, flip to the results view whenever a deal or preview opens
  const tabState = useWorkspaceTabs(deal, expert, results, () => setShowChat(false));
  const closers = useWorkspaceClosers(deal, expert, results, tabState);

  const tabs = buildTabBarProps({ deal, expert, results, tabState, closers, dispatch });

  const content = (
    <WorkspaceTabContent
      activeTab={tabState.activeTab}
      documentTabs={tabState.documentTabs}
      onCloseDocument={closers.closeDocument}
    />
  );

  return (
    <div className="w-full h-full bg-transparent">
      <MobileDealsView
        showChat={showChat}
        onSelectView={setShowChat}
        tabs={tabs}
        content={content}
      />
      <DesktopDealsView
        showWorkspace={results.showWorkspace}
        resize={resize}
        entrance={entrance}
        inlineDealsFlag={deal.inlineDealsFlag}
        onClosePanel={closers.closePanel}
        tabs={tabs}
        content={content}
      />
    </div>
  );
}
